import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, GraduationCap, Utensils, Waves, Loader2, Sparkles, IndianRupee, Users, TrendingUp, Trophy, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useSearchParams, Link, useParams } from "react-router-dom";

const CAUSE_ICONS = { school: GraduationCap, medical: Heart, food: Utensils, disaster: Waves };
const rupees = (paise) => `₹${(paise / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

export default function Donate() {
  const { user } = useAuth();
  const { campaignId } = useParams();
  const [causes, setCauses] = useState({ buckets: [], presets_paise: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedCause, setSelectedCause] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  useEffect(() => {
    api.get("/donations/causes").then(r => setCauses(r.data)).catch(() => {});
    api.get("/donations/campaigns").then(r => setCampaigns(r.data)).catch(() => {});
    api.get("/donations/summary").then(r => setSummary(r.data)).catch(() => {});
    if (campaignId) {
      api.get(`/donations/campaigns/${campaignId}`).then(r => setSelectedCampaign(r.data)).catch(() => {});
    }
  }, [campaignId]);

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-6">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 rounded-3xl p-6 md:p-8">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-rose-700 bg-white border border-rose-200 px-3 py-1 rounded-full mb-3">
          <Sparkles size={12} /> Box of Happiness
        </div>
        <h1 className="heading text-4xl md:text-5xl font-extrabold text-stone-900 leading-tight">Pay from your heart</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">Small giving, big change. Support a school, cover a family&apos;s medical bill, or fund a disaster response — any amount, any cause.</p>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
            <Stat icon={IndianRupee} value={rupees(summary.total_raised_paise)} label="Raised so far" color="text-rose-700 bg-rose-50" />
            <Stat icon={Users} value={summary.total_donations.toLocaleString()} label="Donations" color="text-amber-700 bg-amber-50" />
            <Stat icon={TrendingUp} value={campaigns.length.toString()} label="Active campaigns" color="text-emerald-700 bg-emerald-50" />
          </div>
        )}
      </motion.div>

      {/* Quick-donate buckets */}
      {!selectedCampaign && (
        <>
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-3">Quick donate — pick a cause</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="donate-buckets">
            {causes.buckets.map(b => {
              const Icon = CAUSE_ICONS[b.slug] || Heart;
              const raised = summary?.by_cause_paise?.[b.slug] || 0;
              return (
                <button
                  key={b.slug}
                  onClick={() => setSelectedCause(b)}
                  className="text-left bg-white border-2 border-stone-200 hover:border-rose-500 hover:shadow-md rounded-2xl p-4 transition-all"
                  data-testid={`donate-bucket-${b.slug}`}
                >
                  <div className="w-10 h-10 rounded-xl grid place-items-center mb-2" style={{ background: b.color + "22", color: b.color }}>
                    <Icon size={20} />
                  </div>
                  <div className="font-bold text-sm text-stone-900">{b.label}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{b.description}</div>
                  <div className="text-[11px] font-mono font-bold mt-2" style={{ color: b.color }}>{rupees(raised)} raised</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Selected donation flow */}
      {(selectedCause || selectedCampaign) && (
        <DonationForm
          cause={selectedCause}
          campaign={selectedCampaign}
          presets={causes.presets_paise}
          user={user}
          onClose={() => { setSelectedCause(null); setSelectedCampaign(null); }}
        />
      )}

      {/* Active campaigns */}
      {!selectedCampaign && campaigns.length > 0 && (
        <>
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-3 mt-6">Active campaigns</div>
          <div className="grid md:grid-cols-2 gap-3" data-testid="donate-campaigns">
            {campaigns.map(c => <CampaignCard key={c.campaign_id} c={c} onClick={() => setSelectedCampaign(c)} />)}
          </div>
        </>
      )}

      {/* Leaderboard link */}
      <div className="mt-8 text-center text-xs text-stone-500">
        <Link to="/donate/leaderboard" className="inline-flex items-center gap-1 font-bold hover:text-rose-600" data-testid="donate-leaderboard-link">
          <Trophy size={12} /> See top donors
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-3">
      <div className={`w-8 h-8 rounded-xl ${color} grid place-items-center mb-1.5`}><Icon size={14} /></div>
      <div className="text-xl font-black text-stone-900">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{label}</div>
    </div>
  );
}

function CampaignCard({ c, onClick }) {
  const pct = Math.min(100, Math.round((c.raised_paise / Math.max(1, c.goal_paise)) * 100));
  const Icon = CAUSE_ICONS[c.cause_slug] || Heart;
  return (
    <button onClick={onClick} className="text-left bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`donate-campaign-${c.campaign_id}`}>
      {c.hero_image && <img loading="lazy" src={c.hero_image} alt={c.title} className="w-full h-32 object-cover" />}
      <div className="p-4">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 grid place-items-center shrink-0"><Icon size={14} /></div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm text-stone-900 truncate">{c.title}</div>
            <div className="text-[11px] text-stone-500 truncate">{c.beneficiary_name} · {c.city || "India"}</div>
          </div>
          {c.is_urgent && <span className="text-[10px] font-bold uppercase tracking-widest bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded">Urgent</span>}
        </div>
        {c.story && <div className="text-xs text-stone-600 mt-2 line-clamp-2">{c.story}</div>}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-bold text-rose-700">{rupees(c.raised_paise)}</span>
            <span className="text-stone-500">of {rupees(c.goal_paise)} goal</span>
          </div>
          <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full bg-rose-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] text-stone-500 mt-1">{c.donor_count} donors · {pct}% funded</div>
        </div>
      </div>
    </button>
  );
}

function DonationForm({ cause, campaign, presets, user, onClose }) {
  const [amount, setAmount] = useState(presets?.[2] || 50000);
  const [donorName, setDonorName] = useState(user?.name || "");
  const [message, setMessage] = useState("");
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(true);
  const [customAmount, setCustomAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const targetLabel = campaign?.title || cause?.label || "cause";

  const donate = async () => {
    const finalPaise = customAmount ? Math.round(parseFloat(customAmount) * 100) : amount;
    if (!finalPaise || finalPaise < 1000) return toast.error("Minimum donation is ₹10");
    setProcessing(true);
    try {
      const r = await api.post("/donations/checkout", {
        amount_paise: finalPaise,
        campaign_id: campaign?.campaign_id,
        cause_slug: campaign?.cause_slug || cause?.slug,
        donor_name: donorName || "Anonymous",
        show_on_leaderboard: showOnLeaderboard,
        message: message.slice(0, 200),
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setProcessing(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white border-2 border-rose-100 rounded-3xl p-5 md:p-6 mb-6" data-testid="donation-form">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">You&apos;re donating to</div>
          <div className="heading text-2xl font-extrabold text-stone-900">{targetLabel}</div>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-900 text-2xl" data-testid="donation-close">×</button>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
        {presets.map(p => (
          <button
            key={p}
            onClick={() => { setAmount(p); setCustomAmount(""); }}
            className={`py-3 rounded-xl border-2 font-bold text-sm transition-colors ${amount === p && !customAmount ? "border-rose-500 bg-rose-50 text-rose-700" : "border-stone-200 hover:border-rose-300"}`}
            data-testid={`donate-preset-${p}`}
          >{rupees(p)}</button>
        ))}
      </div>
      <Input
        value={customAmount}
        onChange={e => setCustomAmount(e.target.value)}
        placeholder="Or custom amount ₹"
        type="number"
        className="rounded-xl h-11 mb-3"
        data-testid="donate-custom-amount"
      />

      <div className="grid md:grid-cols-2 gap-3">
        <Input
          value={donorName}
          onChange={e => setDonorName(e.target.value)}
          placeholder="Your name (public if you check leaderboard)"
          className="rounded-xl h-11"
          data-testid="donate-donor-name"
        />
        <label className="flex items-center gap-2 text-xs text-stone-600 px-2">
          <input type="checkbox" checked={showOnLeaderboard} onChange={e => setShowOnLeaderboard(e.target.checked)} className="accent-rose-600" data-testid="donate-leaderboard-toggle" />
          Show me on the public donor wall
        </label>
      </div>
      <Textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Optional message (200 chars) — e.g. 'From Mumbai with love'"
        rows={2}
        maxLength={200}
        className="mt-3 rounded-xl"
        data-testid="donate-message"
      />

      <Button
        onClick={donate}
        disabled={processing}
        className="w-full mt-4 rounded-full bg-rose-600 hover:bg-rose-700 h-12 font-bold text-base"
        data-testid="donate-pay-btn"
      >
        {processing ? <><Loader2 size={16} className="animate-spin mr-2" /> Opening checkout…</> : <><Heart size={16} className="mr-2" /> Donate {rupees(customAmount ? Math.round(parseFloat(customAmount) * 100) || 0 : amount)}</>}
      </Button>
      <div className="text-[10px] text-stone-400 text-center mt-2">
        Secured by Stripe. 100% of your donation reaches the beneficiary (Find2Near covers the payment processing fee).
      </div>
    </motion.div>
  );
}

// ---- Thank-you page (Stripe success redirect) ----
export function DonateThanks() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!sid) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts++;
      try {
        const r = await api.get(`/donations/status/${sid}`);
        if (r.data.payment_status === "paid") { setStatus(r.data); clearInterval(iv); }
        else if (attempts >= 15) { setStatus({ payment_status: "unknown" }); clearInterval(iv); }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(iv);
  }, [sid]);

  return (
    <div className="max-w-lg mx-auto px-5 py-16 text-center">
      {!status ? (
        <><Loader2 size={32} className="animate-spin mx-auto mb-3 text-rose-600" /><div className="text-stone-500">Confirming your donation…</div></>
      ) : status.payment_status === "paid" ? (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <div className="w-20 h-20 rounded-full bg-rose-100 grid place-items-center mx-auto mb-4"><Heart size={36} className="text-rose-600" fill="currentColor" /></div>
          <h1 className="heading text-4xl font-extrabold text-stone-900 mb-2">Thank you!</h1>
          <p className="text-stone-600">Your {rupees(status.amount_paise)} donation is on its way. Every rupee counts.</p>
          <div className="mt-6 flex gap-2 justify-center">
            <Link to="/donate"><Button variant="outline" className="rounded-full">Donate more</Button></Link>
            <Link to="/"><Button className="rounded-full bg-rose-600 hover:bg-rose-700">Explore Find2Near</Button></Link>
          </div>
        </motion.div>
      ) : (
        <div>
          <div className="text-lg font-bold mb-1">Payment pending</div>
          <p className="text-stone-500 text-sm">If your card was charged, the donation will appear soon. Contact ops@find2near if anything looks off.</p>
        </div>
      )}
    </div>
  );
}

// ---- Public leaderboard ----
export function DonateLeaderboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/donations/leaderboard").then(r => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-2xl mx-auto px-5 py-6">
      <div className="flex items-center gap-2 mb-4">
        <Trophy size={22} className="text-amber-500" />
        <h1 className="heading text-3xl font-extrabold text-stone-900">Donor wall</h1>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-stone-500">Be the first! <Link to="/donate" className="text-rose-600 font-bold">Donate now</Link></div>
      ) : (
        <div className="space-y-2" data-testid="donate-leaderboard">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-800 font-black text-xs grid place-items-center">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-stone-900 truncate">{r.donor_name || "Anonymous"}</div>
                {r.message && <div className="text-[11px] text-stone-500 italic truncate">&ldquo;{r.message}&rdquo;</div>}
              </div>
              <div className="font-mono font-bold text-rose-700">{rupees(r.amount_paise)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
